/* eslint-disable no-underscore-dangle */
import React, { useRef } from 'react';

import { queueCartWrite } from '../../../api/cart';
import { trackAndWait } from '../../../analytics/shared';
import { getAbCheckoutCartAttributes } from '../../../shared/ab-checkout-attributes';
import { getReferralCheckoutCartAttributes } from '../../../shared/referral-checkout-attributes';
import { ABTestGroup } from '../../../shared/ab_test';
import { addNavigationLoadingState, hasLoadingState } from '../../../shared/loading-state-helpers';
import { PRODUCTS_DATA_KEY } from '../../../utils/session-storage-keys';
import SessionStorage from '../../../utils/session-storage';

import { checkIsUpsellOnlyPage } from '../helpers/checkPage';

const MINIMUM_UPSELL_PRICE = 9000;
const MINIMUM_CART_PRICE = 7000;
const CHECKOUT_ATTRIBUTES_TIMEOUT_MS = 1500;

export const FooterContainer = ({ cart, Component }) => {
  const buttonRef = useRef();

  if (cart.item_count === 0) {
    return null;
  }

  const isUpsellOnlyPage = checkIsUpsellOnlyPage();

  const totalQuantity = cart.items.reduce((sum, item) => {
    const variantData = window.variantDataSelector.getData(item.variant_id) || {};
    const { otpPrice, subscriptionPrice } = variantData;

    const isSubscribedItem = Boolean(item.properties._subscription_id);
    const discountedPrice = isSubscribedItem ? subscriptionPrice : otpPrice;

    return sum + (discountedPrice * item.quantity);
  }, 0);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (hasLoadingState(buttonRef.current)) {
      return;
    }

    if (buttonRef.current) {
      addNavigationLoadingState(buttonRef.current);
    }

    const productsData = cart.items.map((item) => ({
      variantId: item.variant_id,
      quantity: item.quantity,
      subscriptionId: item?.properties?._subscription_id,
      purchase_type: item?.properties?._subscription_id ? 'subscription' : 'one-time',
      uniqToken: window?.ShopifyAnalytics?.lib?.trekkie?.defaultAttributes?.uniqToken
    }));

    const checkoutTracking = Promise.all(productsData.map((productData) => trackAndWait({
      eventName: 'checkout_initiated',
      data: {
        line_item_properties: productData
      }
    })));

    const referralAttributes = await getReferralCheckoutCartAttributes();

    const attributesWrite = queueCartWrite(() => fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          path: window.location.pathname,
          ab_entry_url: ABTestGroup.get(),
          ...getAbCheckoutCartAttributes(),
          ...referralAttributes
        }
      })
    }))
      .then((response) => response.json())
      .then(() => {
        // eslint-disable-next-line no-console
        console.log('Cart updated with custom token.');
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error:', error);
      });

    try {
      SessionStorage.set(PRODUCTS_DATA_KEY, productsData);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error:', error);
    }

    const timeout = new Promise((resolve) => {
      setTimeout(resolve, CHECKOUT_ATTRIBUTES_TIMEOUT_MS);
    });

    await Promise.all([Promise.race([attributesWrite, timeout]), checkoutTracking]);

    window.location.href = '/checkout';
  };

  const props = {
    totalQuantity,
    buttonRef,
    isFreeShipping: totalQuantity >= MINIMUM_CART_PRICE,
    isDisabled: isUpsellOnlyPage && totalQuantity < MINIMUM_UPSELL_PRICE,
    onSubmit: handleSubmit
  };

  return <Component {...props} />;
};
